/**
 * F40 (Wave 2 Track D — admin) — admin promotions moderation + analytics client.
 *
 * Endpoints (apps/api/src/routes/admin/promotions.ts, registered under `/admin`):
 *   GET  /admin/promotions               → listPromotions (status/venueId/suspended/page/perPage)
 *   GET  /admin/promotions/analytics     → getAnalytics (platform rollup)
 *   GET  /admin/promotions/:id           → getPromotion (detail + metrics + redemptions)
 *   POST /admin/promotions/:id/suspend   → suspendPromotion (`promotions:moderate`, reason)
 *   POST /admin/promotions/:id/restore   → restorePromotion (`promotions:moderate`)
 *
 * Reads are gated server-side by `promotions:read` (super_admin + finance +
 * support + content_moderator); suspend/restore by `promotions:moderate`
 * (super_admin + content_moderator only — finance is read-only). The page-level
 * <ProtectedRoute> gates which routes a role can navigate to; the in-page
 * suspend/restore CTAs are additionally gated on `promotions:moderate`. The
 * server re-enforces RBAC and audits every mutation via F06's `onSend` hook.
 *
 * Component tests do NOT exercise this real client — the vitest config alias
 * rewrites `./api/promotions` → `./api/promotions.fixture` (the F23/F38/F39
 * admin convention).
 *
 * Suspension is ORTHOGONAL to the lifecycle `status` (sets `suspendedAt` +
 * `suspensionReason`; the `status` value is unchanged) — mirrors F32 open-games.
 */
import type {
  Promotion,
  PromotionMetrics,
  PromotionRedemption,
  PromotionStatus,
  PromotionDisplayMode,
  SuspendPromotionInput,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

interface DataEnvelope<T> {
  readonly data: T;
}

// Re-export the shared ApiError so callers/tests can `instanceof`-check the
// same class the rest of the admin app throws.
export { ApiError as AdminPromotionError } from '../auth/types';

// ─── Read-side view types ────────────────────────────────────────────────────

/** One row of the cross-venue admin promotions list. */
export interface AdminPromotionRow extends Promotion {
  /** Resolved venue display name (server-side join, when present). */
  readonly venueName?: string | null;
  readonly venueSlug?: string | null;
}

export interface AdminPromotionsListResponse {
  readonly data: readonly AdminPromotionRow[];
  readonly pagination: {
    readonly page: number;
    readonly perPage: number;
    readonly total: number;
  };
}

/**
 * Promotion detail: the row + aggregated metrics + the redemption ledger. The
 * server's `GET /admin/promotions/:id` returns `{ promotion, metrics,
 * redemptions }` (rev-05 — the newest-50 `promotion_redemptions` ledger,
 * camelCase). The client still defaults `redemptions` to `[]` defensively so
 * the page never crashes on `.length` access against an older server.
 */
export interface AdminPromotionDetailResponse {
  readonly promotion: AdminPromotionRow;
  readonly metrics: PromotionMetrics;
  readonly redemptions: readonly PromotionRedemption[];
}

/** Platform-wide promotion analytics (the `/admin/promotions/analytics` rollup). */
export interface AdminPromotionAnalyticsResponse {
  /** Promotion-status distribution. */
  readonly byStatus: ReadonlyArray<{ readonly status: PromotionStatus; readonly count: number }>;
  readonly suspendedCount: number;
  readonly totalRedemptions: number;
  readonly totalDiscountMinor: number;
  readonly distinctUsers: number;
  /** Top promotions by redemption count. */
  readonly topPromotions: ReadonlyArray<{
    readonly promotionId: string;
    readonly venueId: string;
    readonly code: string | null;
    readonly redemptions: number;
    readonly discountMinor: number;
  }>;
  /** Redemptions over time (server emits this for the line chart when available). */
  readonly redemptionsByDay?: ReadonlyArray<{ readonly day: string; readonly count: number }>;
}

// ─── Query option types ───────────────────────────────────────────────────────

export interface ListPromotionsOpts {
  readonly status?: PromotionStatus | 'all';
  /**
   * `displayMode` is filtered CLIENT-side (the server list endpoint does not
   * accept it). Forwarded only so callers express the intended filter; the
   * fixture honours it, the real client narrows the returned rows.
   */
  readonly displayMode?: PromotionDisplayMode | 'all';
  /** Venue id filter (the server's only venue filter — the UI surfaces it as a search box). */
  readonly venueId?: string;
  readonly suspended?: boolean;
  readonly page?: number;
  readonly perPage?: number;
}

function buildListQuery(opts: ListPromotionsOpts): string {
  const params: Record<string, string | number | boolean | undefined | null> = {
    venueId: opts.venueId,
    page: opts.page,
    perPage: opts.perPage,
  };
  if (opts.status && opts.status !== 'all') params.status = opts.status;
  if (opts.suspended !== undefined) params.suspended = opts.suspended;
  return buildQuery(params);
}

function isNotFound(err: unknown): boolean {
  return (
    err !== null &&
    typeof err === 'object' &&
    'status' in err &&
    (err as { status: number }).status === 404
  );
}

function matchesDisplayMode(
  rows: readonly AdminPromotionRow[],
  mode?: PromotionDisplayMode | 'all',
) {
  if (!mode || mode === 'all') return rows;
  return rows.filter((r) => r.displayMode === mode);
}

// ─── API surface ───────────────────────────────────────────────────────────────

export const adminPromotionsApi = {
  /**
   * GET /admin/promotions — paginated cross-venue list. `displayMode` is
   * narrowed client-side (the server endpoint has no displayMode filter).
   */
  listPromotions: async (opts?: ListPromotionsOpts): Promise<AdminPromotionsListResponse> => {
    const o = opts ?? {};
    const raw = await adminFetch<AdminPromotionsListResponse>(
      `/admin/promotions${buildListQuery(o)}`,
    );
    const data = matchesDisplayMode(raw.data, o.displayMode);
    return { data, pagination: raw.pagination };
  },

  /** GET /admin/promotions/:id — detail + metrics + redemptions. */
  getPromotion: async (id: string): Promise<AdminPromotionDetailResponse | null> => {
    try {
      const res = await adminFetch<DataEnvelope<Partial<AdminPromotionDetailResponse>>>(
        `/admin/promotions/${encodeURIComponent(id)}`,
      );
      const d = res.data;
      return {
        promotion: d.promotion as AdminPromotionRow,
        metrics: d.metrics as PromotionMetrics,
        redemptions: d.redemptions ?? [],
      };
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  },

  /**
   * POST /admin/promotions/:id/suspend — orthogonal suspension. Sets
   * `suspendedAt` + `suspensionReason`; the lifecycle `status` is unchanged.
   * Mandatory reason. Gated server-side on `promotions:moderate`. Audited.
   */
  suspendPromotion: async (
    id: string,
    input: SuspendPromotionInput,
  ): Promise<AdminPromotionRow> => {
    const res = await adminFetch<DataEnvelope<AdminPromotionRow>>(
      `/admin/promotions/${encodeURIComponent(id)}/suspend`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return res.data;
  },

  /**
   * POST /admin/promotions/:id/restore — clear the suspension. Gated
   * server-side on `promotions:moderate`. Audited.
   */
  restorePromotion: async (id: string): Promise<AdminPromotionRow> => {
    const res = await adminFetch<DataEnvelope<AdminPromotionRow>>(
      `/admin/promotions/${encodeURIComponent(id)}/restore`,
      { method: 'POST' },
    );
    return res.data;
  },

  /** GET /admin/promotions/analytics — platform-wide rollup. */
  getAnalytics: async (): Promise<AdminPromotionAnalyticsResponse> => {
    const res = await adminFetch<DataEnvelope<AdminPromotionAnalyticsResponse>>(
      '/admin/promotions/analytics',
    );
    return res.data;
  },
};
